/**
 * iOS SDK config — operator console (feature #183).
 *
 * Three-panel dashboard for the `/api/v1/ios-sdk` surface:
 *
 *   1. Register app — bundle_id (reverse-DNS) + app name + optional
 *      team_id + optional min_sdk_version. On create the server returns
 *      the full `IosApp`; on conflict we toast "bundle_id_taken".
 *   2. Apps table — lists registered apps with status, bundle, team,
 *      updated_at, and Edit/Archive actions. Clicking a row opens the
 *      per-app config editor.
 *   3. Config editor — toggle enabled/disabled events (incl. known
 *      built-ins), set a rate-limit-per-minute integer, add/remove
 *      feature flags, pick a min_sdk_version. Displays a diff preview
 *      before Save via `summariseConfigDiff`.
 *   4. Installations pane — recent SDK handshakes, optionally filtered
 *      by the selected app.
 *
 * Brand: emerald->sky gradient headline, zinc 6-8px rounded borders,
 * Inter, light theme. No emojis. No competitor names.
 *
 * Kept monolithic on purpose — splitting sub-components into siblings
 * would widen the owned-file surface.
 */

import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
	configDraftSchema,
	deleteApp,
	describeStatus,
	EMPTY_CONFIG_DRAFT,
	EMPTY_REGISTER_DRAFT,
	fetchConfig,
	formatTs,
	iosSdkQueryKeys,
	KNOWN_EVENTS,
	listApps,
	listInstallations,
	MAX_RATE_LIMIT_PER_MIN,
	MIN_RATE_LIMIT_PER_MIN,
	patchApp,
	registerApp,
	registerDraftSchema,
	summariseConfigDiff,
	toConfigDraft,
	type ConfigDiffSummary,
	type ConfigDraft,
	type Installation,
	type IosApp,
	type RegisterDraft,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens (brand rules)
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const BADGE_OK =
	"inline-flex items-center rounded-[6px] border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700";
const BADGE_WARN =
	"inline-flex items-center rounded-[6px] border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700";
const BADGE_NEUTRAL =
	"inline-flex items-center rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs font-medium text-zinc-700";

function toneToBadge(tone: "ok" | "warn" | "neutral"): string {
	switch (tone) {
		case "ok":
			return BADGE_OK;
		case "warn":
			return BADGE_WARN;
		case "neutral":
			return BADGE_NEUTRAL;
	}
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function IosSdkPage() {
	const qc = useQueryClient();

	const [registerDraft, setRegisterDraft] = useState<RegisterDraft>(
		EMPTY_REGISTER_DRAFT,
	);
	const [registerErrors, setRegisterErrors] = useState<Record<string, string>>(
		{},
	);

	const [selectedAppId, setSelectedAppId] = useState<string | null>(null);
	const [configDraft, setConfigDraft] =
		useState<ConfigDraft>(EMPTY_CONFIG_DRAFT);
	const [configErrors, setConfigErrors] = useState<Record<string, string>>({});

	const [installFilterMine, setInstallFilterMine] = useState<boolean>(true);
	const [newFlagKey, setNewFlagKey] = useState<string>("");

	// ---- Queries ---------------------------------------------------------

	const appsQuery = useQuery({
		queryKey: iosSdkQueryKeys.apps(),
		queryFn: listApps,
		staleTime: 10_000,
	});

	const selectedApp = useMemo<IosApp | null>(() => {
		if (!selectedAppId) return null;
		return (appsQuery.data ?? []).find((a) => a.id === selectedAppId) ?? null;
	}, [appsQuery.data, selectedAppId]);

	const configQuery = useQuery({
		queryKey: iosSdkQueryKeys.config(selectedAppId ?? ""),
		queryFn: () => fetchConfig(selectedAppId ?? ""),
		enabled: Boolean(selectedAppId),
		staleTime: 10_000,
	});

	const installationsQuery = useQuery({
		queryKey: iosSdkQueryKeys.installations(
			installFilterMine ? selectedAppId ?? undefined : undefined,
		),
		queryFn: () =>
			listInstallations({
				appId: installFilterMine ? selectedAppId ?? undefined : undefined,
				limit: 50,
			}),
		staleTime: 5_000,
	});

	// ---- Mutations -------------------------------------------------------

	const registerMutation = useMutation({
		mutationFn: registerApp,
		onSuccess: (outcome) => {
			if (outcome.kind === "created" && outcome.app) {
				toast.success(`Registered ${outcome.app.bundle_id}.`);
				setRegisterDraft(EMPTY_REGISTER_DRAFT);
				setSelectedAppId(outcome.app.id);
				setConfigDraft(toConfigDraft(outcome.app.config));
			} else if (outcome.kind === "bundle_taken") {
				toast.error(outcome.message ?? "Bundle ID already registered.");
			}
			qc.invalidateQueries({ queryKey: iosSdkQueryKeys.apps() });
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to register app.");
		},
	});

	const saveConfigMutation = useMutation({
		mutationFn: ({
			appId,
			patch,
		}: {
			appId: string;
			patch: Parameters<typeof patchApp>[1];
		}) => patchApp(appId, patch),
		onSuccess: (app) => {
			toast.success(`Config saved for ${app.bundle_id}.`);
			qc.invalidateQueries({ queryKey: iosSdkQueryKeys.apps() });
			qc.invalidateQueries({ queryKey: iosSdkQueryKeys.config(app.id) });
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to save config.");
		},
	});

	const archiveMutation = useMutation({
		mutationFn: deleteApp,
		onSuccess: () => {
			toast.success("App archived.");
			setSelectedAppId(null);
			qc.invalidateQueries({ queryKey: iosSdkQueryKeys.apps() });
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to archive app.");
		},
	});

	const toggleActiveMutation = useMutation({
		mutationFn: ({ appId, active }: { appId: string; active: boolean }) =>
			patchApp(appId, { active }),
		onSuccess: (app) => {
			toast.success(
				`${app.bundle_id} is now ${app.active ? "active" : "disabled"}.`,
			);
			qc.invalidateQueries({ queryKey: iosSdkQueryKeys.apps() });
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to toggle status.");
		},
	});

	// ---- Handlers --------------------------------------------------------

	const handleRegisterSubmit = useCallback(
		(e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault();
			const parsed = registerDraftSchema.safeParse(registerDraft);
			if (!parsed.success) {
				setRegisterErrors(parsed.errors);
				return;
			}
			setRegisterErrors({});
			registerMutation.mutate(parsed.data);
		},
		[registerDraft, registerMutation],
	);

	const handleSelectApp = useCallback((app: IosApp) => {
		setSelectedAppId(app.id);
		setConfigDraft(toConfigDraft(app.config));
		setConfigErrors({});
		setNewFlagKey("");
	}, []);

	const handleArchive = useCallback(
		(appId: string) => {
			if (!window.confirm("Archive this app? It can be restored by staff.")) {
				return;
			}
			archiveMutation.mutate(appId);
		},
		[archiveMutation],
	);

	const handleConfigSave = useCallback(
		(e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault();
			if (!selectedApp) {
				toast.error("Select an app first.");
				return;
			}
			const parsed = configDraftSchema.safeParse(configDraft);
			if (!parsed.success) {
				setConfigErrors(parsed.errors);
				return;
			}
			setConfigErrors({});
			saveConfigMutation.mutate({
				appId: selectedApp.id,
				patch: { config: parsed.data },
			});
		},
		[configDraft, saveConfigMutation, selectedApp],
	);

	const diffPreview = useMemo<ConfigDiffSummary | null>(() => {
		if (!selectedApp) return null;
		const parsed = configDraftSchema.safeParse(configDraft);
		if (!parsed.success) return null;
		return summariseConfigDiff(selectedApp.config, parsed.data);
	}, [configDraft, selectedApp]);

	const toggleEvent = useCallback(
		(event: string, group: "enabled" | "disabled") => {
			setConfigDraft((prev) => {
				const list =
					group === "enabled" ? prev.enabledEvents : prev.disabledEvents;
				const next = list.includes(event)
					? list.filter((e) => e !== event)
					: [...list, event];
				return group === "enabled"
					? { ...prev, enabledEvents: next }
					: { ...prev, disabledEvents: next };
			});
		},
		[],
	);

	const setFlag = useCallback((key: string, value: boolean) => {
		setConfigDraft((prev) => ({
			...prev,
			featureFlags: { ...prev.featureFlags, [key]: value },
		}));
	}, []);

	const removeFlag = useCallback((key: string) => {
		setConfigDraft((prev) => {
			const next = { ...prev.featureFlags };
			delete next[key];
			return { ...prev, featureFlags: next };
		});
	}, []);

	const addFlag = useCallback(() => {
		const key = newFlagKey.trim();
		if (!key) return;
		setConfigDraft((prev) => ({
			...prev,
			featureFlags: { ...prev.featureFlags, [key]: false },
		}));
		setNewFlagKey("");
	}, [newFlagKey]);

	return (
		<div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 pb-16 pt-6">
			<header className="flex flex-col gap-2">
				<h1 className="font-inter bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-2xl font-semibold tracking-tight text-transparent">
					iOS SDK configuration
				</h1>
				<p className="max-w-3xl text-sm text-zinc-600">
					Register your iOS bundles (CocoaPods + SPM), tune what each app
					emits at runtime, and watch installations land as the SDK rolls
					out.
				</p>
				<div className="flex flex-wrap items-center gap-2 pt-1">
					<code className="rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-0.5 font-mono text-xs text-zinc-700">
						GET /api/v1/ios-sdk/config
					</code>
					<code className="rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-0.5 font-mono text-xs text-zinc-700">
						POST /api/v1/ios-sdk/apps
					</code>
					<code className="rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-0.5 font-mono text-xs text-zinc-700">
						POST /api/v1/ios-sdk/installations
					</code>
				</div>
			</header>

			<RegisterAppSection
				draft={registerDraft}
				errors={registerErrors}
				pending={registerMutation.isPending}
				onChange={setRegisterDraft}
				onSubmit={handleRegisterSubmit}
			/>

			<AppsTableSection
				apps={appsQuery.data ?? []}
				loading={appsQuery.isLoading}
				fetching={appsQuery.isFetching}
				selectedAppId={selectedAppId}
				busy={toggleActiveMutation.isPending || archiveMutation.isPending}
				onSelect={handleSelectApp}
				onToggle={(app) =>
					toggleActiveMutation.mutate({ appId: app.id, active: !app.active })
				}
				onArchive={(app) => handleArchive(app.id)}
				onRefresh={() => appsQuery.refetch()}
			/>

			{selectedApp ? (
				<ConfigEditorSection
					app={selectedApp}
					draft={configDraft}
					errors={configErrors}
					diff={diffPreview}
					pending={saveConfigMutation.isPending}
					configLoading={configQuery.isLoading}
					configBundleId={configQuery.data?.bundle_id}
					upgradeRecommended={configQuery.data?.upgrade_recommended ?? false}
					newFlagKey={newFlagKey}
					onNewFlagKey={setNewFlagKey}
					onToggleEvent={toggleEvent}
					onRateChange={(rate) =>
						setConfigDraft((prev) => ({ ...prev, rateLimitPerMin: rate }))
					}
					onMinVersionChange={(v) =>
						setConfigDraft((prev) => ({ ...prev, minSdkVersion: v }))
					}
					onFlag={setFlag}
					onRemoveFlag={removeFlag}
					onAddFlag={addFlag}
					onReset={() => setConfigDraft(toConfigDraft(selectedApp.config))}
					onClose={() => setSelectedAppId(null)}
					onSubmit={handleConfigSave}
				/>
			) : null}

			<InstallationsSection
				installations={installationsQuery.data ?? []}
				loading={installationsQuery.isLoading}
				fetching={installationsQuery.isFetching}
				filterMine={installFilterMine}
				hasSelection={Boolean(selectedApp)}
				onToggleFilter={() => setInstallFilterMine((v) => !v)}
				onRefresh={() => installationsQuery.refetch()}
			/>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Register app
// ---------------------------------------------------------------------------

interface RegisterAppSectionProps {
	draft: RegisterDraft;
	errors: Record<string, string>;
	pending: boolean;
	onChange: (updater: (prev: RegisterDraft) => RegisterDraft) => void;
	onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}

function RegisterAppSection({
	draft,
	errors,
	pending,
	onChange,
	onSubmit,
}: RegisterAppSectionProps) {
	return (
		<section aria-labelledby="register-heading" className={`${CARD} p-6`}>
			<h2
				id="register-heading"
				className="text-base font-semibold text-zinc-900"
			>
				Register an iOS app
			</h2>
			<p className={`${HINT} mt-1`}>
				Bundle ID is reverse-DNS. App name is shown on the dashboard. Apple
				Team ID and minimum SDK version are optional.
			</p>
			<form
				onSubmit={onSubmit}
				className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2"
			>
				<Field
					label="Bundle ID"
					name="bundleId"
					value={draft.bundleId}
					onChange={(v) => onChange((prev) => ({ ...prev, bundleId: v }))}
					placeholder="com.acme.app"
					error={errors.bundleId}
					hint="Required. Reverse-DNS, e.g. com.acme.app."
					disabled={pending}
				/>
				<Field
					label="App name"
					name="appName"
					value={draft.appName}
					onChange={(v) => onChange((prev) => ({ ...prev, appName: v }))}
					placeholder="Acme iOS"
					error={errors.appName}
					hint="Required."
					disabled={pending}
				/>
				<Field
					label="Apple Team ID"
					name="teamId"
					value={draft.teamId}
					onChange={(v) => onChange((prev) => ({ ...prev, teamId: v }))}
					placeholder="ABCDE12345"
					error={errors.teamId}
					hint="Optional. Ten uppercase alphanumerics."
					disabled={pending}
				/>
				<Field
					label="Minimum SDK version"
					name="minSdkVersion"
					value={draft.minSdkVersion}
					onChange={(v) =>
						onChange((prev) => ({ ...prev, minSdkVersion: v }))
					}
					placeholder="1.0.0"
					error={errors.minSdkVersion}
					hint="Optional. Strict semver."
					disabled={pending}
				/>
				<div className="md:col-span-2">
					<button type="submit" className={BTN_PRIMARY} disabled={pending}>
						{pending ? "Registering..." : "Register app"}
					</button>
				</div>
			</form>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Apps table
// ---------------------------------------------------------------------------

interface AppsTableSectionProps {
	apps: IosApp[];
	loading: boolean;
	fetching: boolean;
	selectedAppId: string | null;
	busy: boolean;
	onSelect: (app: IosApp) => void;
	onToggle: (app: IosApp) => void;
	onArchive: (app: IosApp) => void;
	onRefresh: () => void;
}

function AppsTableSection({
	apps,
	loading,
	fetching,
	selectedAppId,
	busy,
	onSelect,
	onToggle,
	onArchive,
	onRefresh,
}: AppsTableSectionProps) {
	return (
		<section aria-labelledby="apps-heading" className={`${CARD} p-6`}>
			<div className="flex items-start justify-between gap-4">
				<div>
					<h2
						id="apps-heading"
						className="text-base font-semibold text-zinc-900"
					>
						Registered apps
					</h2>
					<p className={`${HINT} mt-1`}>
						Click a row to edit its runtime config. Archiving is reversible by
						staff.
					</p>
				</div>
				<button
					type="button"
					className={BTN_SECONDARY}
					onClick={onRefresh}
					disabled={fetching}
				>
					{fetching ? "Refreshing..." : "Refresh"}
				</button>
			</div>
			<div className="mt-4 overflow-x-auto">
				<table className="w-full table-fixed border-separate border-spacing-0 text-sm">
					<thead>
						<tr className="text-left text-xs font-medium text-zinc-500">
							<th className="border-b border-zinc-200 px-3 py-2 w-[30%]">
								Bundle ID
							</th>
							<th className="border-b border-zinc-200 px-3 py-2 w-[22%]">
								Name
							</th>
							<th className="border-b border-zinc-200 px-3 py-2 w-[12%]">
								Team
							</th>
							<th className="border-b border-zinc-200 px-3 py-2 w-[10%]">
								Min SDK
							</th>
							<th className="border-b border-zinc-200 px-3 py-2 w-[10%]">
								Status
							</th>
							<th className="border-b border-zinc-200 px-3 py-2 w-[16%]">
								Actions
							</th>
						</tr>
					</thead>
					<tbody>
						{loading ? (
							<tr>
								<td className="px-3 py-3 text-zinc-500" colSpan={6}>
									Loading registered apps...
								</td>
							</tr>
						) : apps.length === 0 ? (
							<tr>
								<td className="px-3 py-3 text-zinc-500" colSpan={6}>
									No apps registered yet.
								</td>
							</tr>
						) : (
							apps.map((app) => (
								<AppRow
									key={app.id}
									app={app}
									selected={app.id === selectedAppId}
									busy={busy}
									onSelect={() => onSelect(app)}
									onToggle={() => onToggle(app)}
									onArchive={() => onArchive(app)}
								/>
							))
						)}
					</tbody>
				</table>
			</div>
		</section>
	);
}

interface AppRowProps {
	app: IosApp;
	selected: boolean;
	busy: boolean;
	onSelect: () => void;
	onToggle: () => void;
	onArchive: () => void;
}

function AppRow({
	app,
	selected,
	busy,
	onSelect,
	onToggle,
	onArchive,
}: AppRowProps) {
	const status = describeStatus(app.active);
	return (
		<tr className={selected ? "bg-emerald-50/40" : "hover:bg-zinc-50"}>
			<td className="border-b border-zinc-100 px-3 py-2 font-mono text-xs text-zinc-900">
				<button
					type="button"
					onClick={onSelect}
					className="text-left hover:underline"
				>
					{app.bundle_id}
				</button>
			</td>
			<td className="border-b border-zinc-100 px-3 py-2 text-zinc-800">
				{app.app_name}
			</td>
			<td className="border-b border-zinc-100 px-3 py-2 font-mono text-xs text-zinc-700">
				{app.team_id ?? "—"}
			</td>
			<td className="border-b border-zinc-100 px-3 py-2 font-mono text-xs text-zinc-700">
				{app.min_sdk_version}
			</td>
			<td className="border-b border-zinc-100 px-3 py-2">
				<span className={toneToBadge(status.tone)}>{status.label}</span>
			</td>
			<td className="border-b border-zinc-100 px-3 py-2">
				<div className="flex flex-wrap items-center gap-2">
					<button
						type="button"
						className={BTN_SECONDARY}
						onClick={onSelect}
						disabled={busy}
					>
						Edit
					</button>
					<button
						type="button"
						className={BTN_SECONDARY}
						onClick={onToggle}
						disabled={busy}
					>
						{app.active ? "Disable" : "Enable"}
					</button>
					<button
						type="button"
						className={BTN_DANGER}
						onClick={onArchive}
						disabled={busy}
					>
						Archive
					</button>
				</div>
			</td>
		</tr>
	);
}

// ---------------------------------------------------------------------------
// Config editor
// ---------------------------------------------------------------------------

interface ConfigEditorSectionProps {
	app: IosApp;
	draft: ConfigDraft;
	errors: Record<string, string>;
	diff: ConfigDiffSummary | null;
	pending: boolean;
	configLoading: boolean;
	configBundleId: string | undefined;
	upgradeRecommended: boolean;
	newFlagKey: string;
	onNewFlagKey: (v: string) => void;
	onToggleEvent: (event: string, group: "enabled" | "disabled") => void;
	onRateChange: (rate: string) => void;
	onMinVersionChange: (v: string) => void;
	onFlag: (key: string, value: boolean) => void;
	onRemoveFlag: (key: string) => void;
	onAddFlag: () => void;
	onReset: () => void;
	onClose: () => void;
	onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}

function ConfigEditorSection(props: ConfigEditorSectionProps) {
	const {
		app,
		draft,
		errors,
		diff,
		pending,
		configLoading,
		configBundleId,
		upgradeRecommended,
		newFlagKey,
		onNewFlagKey,
		onToggleEvent,
		onRateChange,
		onMinVersionChange,
		onFlag,
		onRemoveFlag,
		onAddFlag,
		onReset,
		onClose,
		onSubmit,
	} = props;

	const allEvents = useMemo(() => {
		const set = new Set<string>(KNOWN_EVENTS);
		for (const e of draft.enabledEvents) set.add(e);
		for (const e of draft.disabledEvents) set.add(e);
		return [...set].sort();
	}, [draft.disabledEvents, draft.enabledEvents]);

	return (
		<section aria-labelledby="config-heading" className={`${CARD} p-6`}>
			<div className="flex items-start justify-between gap-4">
				<div>
					<h2
						id="config-heading"
						className="text-base font-semibold text-zinc-900"
					>
						Config for{" "}
						<span className="font-mono text-zinc-700">{app.bundle_id}</span>
					</h2>
					<p className={`${HINT} mt-1`}>
						The iOS SDK fetches this JSON blob on every boot. Disabled events
						always win over enabled ones.
					</p>
					{upgradeRecommended ? (
						<p className="mt-2 rounded-[6px] border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
							Upgrade recommended — at least one install is below the minimum
							SDK version.
						</p>
					) : null}
					{configLoading ? (
						<p className={`${HINT} mt-2`}>Fetching assembled config...</p>
					) : configBundleId ? (
						<p className={`${HINT} mt-2`}>
							Live config assembled for{" "}
							<span className="font-mono text-zinc-700">
								{configBundleId}
							</span>
							.
						</p>
					) : null}
				</div>
				<button type="button" className={BTN_SECONDARY} onClick={onClose}>
					Close
				</button>
			</div>
			<form onSubmit={onSubmit} className="mt-4 flex flex-col gap-6">
				<EventToggleGrid
					events={allEvents}
					enabled={draft.enabledEvents}
					disabled={draft.disabledEvents}
					onToggle={onToggleEvent}
					error={errors.disabledEvents}
					pending={pending}
				/>

				<RateLimitControl
					value={draft.rateLimitPerMin}
					error={errors.rateLimitPerMin}
					onChange={onRateChange}
					pending={pending}
				/>

				<MinVersionControl
					value={draft.minSdkVersion}
					error={errors.minSdkVersion}
					onChange={onMinVersionChange}
					pending={pending}
				/>

				<FeatureFlagTable
					flags={draft.featureFlags}
					newFlagKey={newFlagKey}
					onNewFlagKey={onNewFlagKey}
					onFlag={onFlag}
					onRemove={onRemoveFlag}
					onAdd={onAddFlag}
					pending={pending}
				/>

				<DiffSummary diff={diff} />

				<div className="flex flex-wrap items-center gap-2">
					<button type="submit" className={BTN_PRIMARY} disabled={pending}>
						{pending ? "Saving..." : "Save config"}
					</button>
					<button
						type="button"
						className={BTN_SECONDARY}
						onClick={onReset}
						disabled={pending}
					>
						Reset
					</button>
				</div>
			</form>
		</section>
	);
}

interface EventToggleGridProps {
	events: string[];
	enabled: string[];
	disabled: string[];
	error?: string;
	onToggle: (event: string, group: "enabled" | "disabled") => void;
	pending: boolean;
}

function EventToggleGrid({
	events,
	enabled,
	disabled,
	error,
	onToggle,
	pending,
}: EventToggleGridProps) {
	return (
		<div className="flex flex-col gap-2">
			<span className={LABEL}>Event routing</span>
			<p className={HINT}>
				Tick an event in the enabled column to send it, and in the disabled
				column to block it. Disabled wins when both are ticked.
			</p>
			<div className="mt-2 overflow-x-auto">
				<table className="w-full border-separate border-spacing-0 text-sm">
					<thead>
						<tr className="text-left text-xs font-medium text-zinc-500">
							<th className="border-b border-zinc-200 px-3 py-2">Event</th>
							<th className="border-b border-zinc-200 px-3 py-2 w-[20%] text-center">
								Enabled
							</th>
							<th className="border-b border-zinc-200 px-3 py-2 w-[20%] text-center">
								Disabled
							</th>
						</tr>
					</thead>
					<tbody>
						{events.map((event) => (
							<tr key={event}>
								<td className="border-b border-zinc-100 px-3 py-2 font-mono text-xs text-zinc-700">
									{event}
								</td>
								<td className="border-b border-zinc-100 px-3 py-2 text-center">
									<input
										type="checkbox"
										name={`enabled-${event}`}
										checked={enabled.includes(event)}
										onChange={() => onToggle(event, "enabled")}
										disabled={pending}
									/>
								</td>
								<td className="border-b border-zinc-100 px-3 py-2 text-center">
									<input
										type="checkbox"
										name={`disabled-${event}`}
										checked={disabled.includes(event)}
										onChange={() => onToggle(event, "disabled")}
										disabled={pending}
									/>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
			{error ? <span className={ERROR_TEXT}>{error}</span> : null}
		</div>
	);
}

interface RateLimitControlProps {
	value: string;
	error?: string;
	onChange: (v: string) => void;
	pending: boolean;
}

function RateLimitControl({
	value,
	error,
	onChange,
	pending,
}: RateLimitControlProps) {
	return (
		<div className="flex flex-col gap-1">
			<label htmlFor="rateLimitPerMin" className={LABEL}>
				Rate limit per minute
			</label>
			<input
				id="rateLimitPerMin"
				name="rateLimitPerMin"
				type="number"
				inputMode="numeric"
				className={INPUT}
				min={MIN_RATE_LIMIT_PER_MIN}
				max={MAX_RATE_LIMIT_PER_MIN}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				disabled={pending}
				placeholder="600"
			/>
			{error ? (
				<span className={ERROR_TEXT}>{error}</span>
			) : (
				<span className={HINT}>
					Between {MIN_RATE_LIMIT_PER_MIN} and{" "}
					{MAX_RATE_LIMIT_PER_MIN.toLocaleString()}. Leave blank to inherit.
				</span>
			)}
		</div>
	);
}

interface MinVersionControlProps {
	value: string;
	error?: string;
	onChange: (v: string) => void;
	pending: boolean;
}

function MinVersionControl({
	value,
	error,
	onChange,
	pending,
}: MinVersionControlProps) {
	return (
		<div className="flex flex-col gap-1">
			<label htmlFor="configMinSdkVersion" className={LABEL}>
				Minimum SDK version (config)
			</label>
			<input
				id="configMinSdkVersion"
				name="configMinSdkVersion"
				type="text"
				className={INPUT}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				disabled={pending}
				placeholder="1.0.0"
			/>
			{error ? (
				<span className={ERROR_TEXT}>{error}</span>
			) : (
				<span className={HINT}>
					Strict semver. SDKs below this version get upgrade_recommended=true.
				</span>
			)}
		</div>
	);
}

interface FeatureFlagTableProps {
	flags: Record<string, boolean>;
	newFlagKey: string;
	onNewFlagKey: (v: string) => void;
	onFlag: (key: string, value: boolean) => void;
	onRemove: (key: string) => void;
	onAdd: () => void;
	pending: boolean;
}

function FeatureFlagTable({
	flags,
	newFlagKey,
	onNewFlagKey,
	onFlag,
	onRemove,
	onAdd,
	pending,
}: FeatureFlagTableProps) {
	const entries = useMemo(
		() =>
			Object.entries(flags).sort(([a], [b]) => a.localeCompare(b)),
		[flags],
	);
	return (
		<div className="flex flex-col gap-2">
			<span className={LABEL}>Feature flags</span>
			<p className={HINT}>
				Boolean flags the SDK queries by name. Add a key below then tick the
				checkbox to enable it.
			</p>
			<div className="flex flex-wrap items-end gap-2">
				<div className="min-w-[280px] flex-1">
					<label htmlFor="newFlagKey" className={LABEL}>
						New flag key
					</label>
					<input
						id="newFlagKey"
						name="newFlagKey"
						type="text"
						className={INPUT}
						value={newFlagKey}
						placeholder="newOnboarding"
						onChange={(e) => onNewFlagKey(e.target.value)}
						disabled={pending}
					/>
				</div>
				<button
					type="button"
					className={BTN_SECONDARY}
					onClick={onAdd}
					disabled={pending || !newFlagKey.trim()}
				>
					Add flag
				</button>
			</div>
			{entries.length === 0 ? (
				<p className={`${HINT} mt-1`}>No feature flags set.</p>
			) : (
				<div className="overflow-x-auto">
					<table className="w-full border-separate border-spacing-0 text-sm">
						<thead>
							<tr className="text-left text-xs font-medium text-zinc-500">
								<th className="border-b border-zinc-200 px-3 py-2">Key</th>
								<th className="border-b border-zinc-200 px-3 py-2 w-[20%] text-center">
									Enabled
								</th>
								<th className="border-b border-zinc-200 px-3 py-2 w-[16%]" />
							</tr>
						</thead>
						<tbody>
							{entries.map(([key, value]) => (
								<tr key={key}>
									<td className="border-b border-zinc-100 px-3 py-2 font-mono text-xs text-zinc-900">
										{key}
									</td>
									<td className="border-b border-zinc-100 px-3 py-2 text-center">
										<input
											type="checkbox"
											name={`flag-${key}`}
											checked={value}
											onChange={(e) => onFlag(key, e.target.checked)}
											disabled={pending}
										/>
									</td>
									<td className="border-b border-zinc-100 px-3 py-2">
										<button
											type="button"
											className={BTN_SECONDARY}
											onClick={() => onRemove(key)}
											disabled={pending}
										>
											Remove
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
}

interface DiffSummaryProps {
	diff: ConfigDiffSummary | null;
}

function DiffSummary({ diff }: DiffSummaryProps) {
	if (!diff || !diff.any) {
		return (
			<p className={HINT}>
				No pending changes — the config matches what the server has.
			</p>
		);
	}
	const parts: { label: string; items: string[] }[] = [];
	if (diff.enabled_added.length > 0) {
		parts.push({ label: "Enabling", items: diff.enabled_added });
	}
	if (diff.enabled_removed.length > 0) {
		parts.push({ label: "No longer enabling", items: diff.enabled_removed });
	}
	if (diff.disabled_added.length > 0) {
		parts.push({ label: "Disabling", items: diff.disabled_added });
	}
	if (diff.disabled_removed.length > 0) {
		parts.push({
			label: "No longer disabling",
			items: diff.disabled_removed,
		});
	}
	if (diff.flags_changed.length > 0) {
		parts.push({ label: "Flag changes", items: diff.flags_changed });
	}
	return (
		<div className="rounded-[6px] border border-emerald-200 bg-emerald-50/50 px-4 py-3">
			<p className="text-sm font-medium text-emerald-900">Pending changes</p>
			<ul className="mt-2 space-y-1 text-xs text-emerald-900/80">
				{parts.map((part) => (
					<li key={part.label}>
						<span className="font-medium">{part.label}:</span>{" "}
						<span className="font-mono">{part.items.join(", ")}</span>
					</li>
				))}
				{diff.rate_changed ? (
					<li>
						<span className="font-medium">Rate limit:</span> updated
					</li>
				) : null}
				{diff.min_sdk_changed ? (
					<li>
						<span className="font-medium">Min SDK version:</span> updated
					</li>
				) : null}
			</ul>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Installations
// ---------------------------------------------------------------------------

interface InstallationsSectionProps {
	installations: Installation[];
	loading: boolean;
	fetching: boolean;
	filterMine: boolean;
	hasSelection: boolean;
	onToggleFilter: () => void;
	onRefresh: () => void;
}

function InstallationsSection({
	installations,
	loading,
	fetching,
	filterMine,
	hasSelection,
	onToggleFilter,
	onRefresh,
}: InstallationsSectionProps) {
	return (
		<section aria-labelledby="installations-heading" className={`${CARD} p-6`}>
			<div className="flex items-start justify-between gap-4">
				<div>
					<h2
						id="installations-heading"
						className="text-base font-semibold text-zinc-900"
					>
						Recent installations
					</h2>
					<p className={`${HINT} mt-1`}>
						Every SDK handshake records a dedup-friendly install fingerprint.
					</p>
				</div>
				<div className="flex items-center gap-2">
					<label className="flex items-center gap-2 text-xs text-zinc-600">
						<input
							type="checkbox"
							name="filterSelected"
							checked={filterMine}
							onChange={onToggleFilter}
							disabled={!hasSelection}
						/>
						Only selected app
					</label>
					<button
						type="button"
						className={BTN_SECONDARY}
						onClick={onRefresh}
						disabled={fetching}
					>
						{fetching ? "Refreshing..." : "Refresh"}
					</button>
				</div>
			</div>
			<div className="mt-4 overflow-x-auto">
				<table className="w-full border-separate border-spacing-0 text-sm">
					<thead>
						<tr className="text-left text-xs font-medium text-zinc-500">
							<th className="border-b border-zinc-200 px-3 py-2">
								When
							</th>
							<th className="border-b border-zinc-200 px-3 py-2">
								Install ID
							</th>
							<th className="border-b border-zinc-200 px-3 py-2">
								SDK version
							</th>
							<th className="border-b border-zinc-200 px-3 py-2">
								Device model
							</th>
							<th className="border-b border-zinc-200 px-3 py-2">
								OS version
							</th>
						</tr>
					</thead>
					<tbody>
						{loading ? (
							<tr>
								<td className="px-3 py-3 text-zinc-500" colSpan={5}>
									Loading installations...
								</td>
							</tr>
						) : installations.length === 0 ? (
							<tr>
								<td className="px-3 py-3 text-zinc-500" colSpan={5}>
									No installations recorded yet.
								</td>
							</tr>
						) : (
							installations.map((row) => (
								<tr key={row.id}>
									<td className="border-b border-zinc-100 px-3 py-2 text-xs text-zinc-700">
										{formatTs(row.created_at)}
									</td>
									<td className="border-b border-zinc-100 px-3 py-2 font-mono text-xs text-zinc-900">
										{row.install_id}
									</td>
									<td className="border-b border-zinc-100 px-3 py-2 font-mono text-xs text-zinc-700">
										{row.sdk_version}
									</td>
									<td className="border-b border-zinc-100 px-3 py-2 text-xs text-zinc-700">
										{row.device_model ?? "—"}
									</td>
									<td className="border-b border-zinc-100 px-3 py-2 text-xs text-zinc-700">
										{row.os_version ?? "—"}
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Shared Field widget
// ---------------------------------------------------------------------------

interface FieldProps {
	label: string;
	name: string;
	value: string;
	onChange: (v: string) => void;
	placeholder?: string;
	hint?: string;
	error?: string;
	disabled?: boolean;
}

function Field({
	label,
	name,
	value,
	onChange,
	placeholder,
	hint,
	error,
	disabled,
}: FieldProps) {
	return (
		<div className="flex flex-col gap-1">
			<label htmlFor={name} className={LABEL}>
				{label}
			</label>
			<input
				id={name}
				name={name}
				type="text"
				className={INPUT}
				placeholder={placeholder}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				disabled={disabled}
				autoComplete="off"
				spellCheck={false}
			/>
			{error ? (
				<span className={ERROR_TEXT}>{error}</span>
			) : hint ? (
				<span className={HINT}>{hint}</span>
			) : null}
		</div>
	);
}

export default IosSdkPage;
